/**
 * Pillar mega-menu.
 *
 * Replaces the React openMenu/closeMenu pair that lived in every pillarsd
 * page's logic class. Timing is deliberately identical to the original: a menu
 * opens immediately on hover or focus, and closes 120ms after the pointer
 * leaves, so you can cross the gap between the nav link and the panel below it.
 *
 * All four panels are already in the DOM (server-rendered by
 * sections/pillar-header.liquid); this only toggles [data-open].
 *
 * Hover listens for pointer events filtered to a mouse, not mouseenter. A tap
 * sends compatibility mouse events too, so on Android one tap opened the panel
 * and followed the link in the same gesture, and the visitor never saw the
 * menu. A touch or pen tap on a nav item now opens its panel first, and a
 * second tap on the same item follows the link. Tapping anywhere outside the
 * nav items and the open panel closes it.
 *
 * Keyboard: the panels come after the header row in the DOM, so Tab alone
 * never reached them. With a nav item focused and its panel open, Tab or
 * ArrowDown moves into the panel. Tab past its last link goes on to the next
 * nav item, and Shift+Tab before its first link goes back to the item. Escape
 * closes it and returns focus to the item, and a panel closes when focus
 * leaves both the item and the panel.
 *
 * The mobile drawer is CSS-only (the #mnav-toggle checkbox). This adds Enter
 * to toggle it, which a checkbox does not do natively, and Escape to close it.
 * It also closes the drawer when one of its links is used, so a same-page
 * link does not leave it covering the page, and when focus moves on past the
 * header.
 */
(function () {
  'use strict';

  var CLOSE_DELAY = 120;
  var DESKTOP_MIN = 881; // below this the nav collapses to the CSS-only drawer
  var TAP_WINDOW = 1000; // ms from a tap's pointerdown to its click

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN;
  }

  function isEscape(e) {
    return e.key === 'Escape' || e.key === 'Esc';
  }

  function bindDrawer(root) {
    var toggle = root.querySelector('#mnav-toggle');
    var nav = root.querySelector('[data-nav]');
    if (!toggle) return;

    toggle.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        toggle.click();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (isEscape(e) && toggle.checked) {
        toggle.checked = false;
        if (!isDesktop()) toggle.focus();
      }
    });
    if (nav) {
      nav.addEventListener('click', function (e) {
        if (toggle.checked && e.target.closest && e.target.closest('a')) toggle.checked = false;
      });
    }
    // Tabbing on past the header's last link would put focus in the page
    // under the open drawer, out of sight.
    root.addEventListener('focusout', function (e) {
      if (toggle.checked && e.relatedTarget && !root.contains(e.relatedTarget)) toggle.checked = false;
    });
    window.addEventListener('resize', function () {
      if (isDesktop()) toggle.checked = false;
    });
  }

  function init(root) {
    if (!root || root.dataset.headerBound === '1') return;
    root.dataset.headerBound = '1';

    bindDrawer(root);

    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-menu-trigger]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-menu-panel]'));
    if (!triggers.length || !panels.length) return;

    var timer = null;
    var openKey = null;
    var mouseInside = false;
    var quietFocus = false; // true while we move focus back to a trigger ourselves
    var tap = null; // the touch/pen pointerdown on a trigger, awaiting its click

    function find(list, attr, key) {
      for (var i = 0; i < list.length; i++) {
        if (list[i].getAttribute(attr) === key) return list[i];
      }
      return null;
    }

    function triggerFor(key) {
      return find(triggers, 'data-menu-trigger', key);
    }

    function panelFor(key) {
      return find(panels, 'data-menu-panel', key);
    }

    function links(panel) {
      return panel ? Array.prototype.slice.call(panel.querySelectorAll('a[href]')) : [];
    }

    // Inside a nav item or a panel of this header.
    function inMenu(el) {
      if (!el || !el.closest || !root.contains(el)) return false;
      return !!(el.closest('[data-menu-trigger]') || el.closest('[data-menu-panel]'));
    }

    function paint() {
      var desktop = isDesktop();
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].getAttribute('data-menu-panel') === openKey) panels[i].setAttribute('data-open', '');
        else panels[i].removeAttribute('data-open');
      }
      // A nav item is a disclosure only on desktop; in the drawer it is a link.
      for (var j = 0; j < triggers.length; j++) {
        var t = triggers[j];
        if (desktop) t.setAttribute('aria-expanded', t.getAttribute('data-menu-trigger') === openKey ? 'true' : 'false');
        else t.removeAttribute('aria-expanded');
      }
    }

    function open(key) {
      clearTimeout(timer);
      if (openKey === key) return;
      openKey = key;
      paint();
    }

    function close() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        openKey = null;
        paint();
      }, CLOSE_DELAY);
    }

    function closeNow() {
      clearTimeout(timer);
      openKey = null;
      paint();
    }

    function focusTrigger(key) {
      var t = triggerFor(key);
      if (!t) return;
      quietFocus = true;
      t.focus();
      quietFocus = false;
    }

    // Where Tab goes after the last link of a panel: the next nav item, or
    // the header button after the last one.
    function nextStop(key) {
      var i = triggers.indexOf(triggerFor(key));
      if (i > -1 && i < triggers.length - 1) return triggers[i + 1];
      return root.querySelector('[data-header-cta] a, [data-header-cta] button');
    }

    triggers.forEach(function (el) {
      var key = el.getAttribute('data-menu-trigger');

      el.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse' && isDesktop()) open(key);
      });
      el.addEventListener('focus', function () {
        if (!quietFocus && isDesktop()) open(key);
      });

      // Touch and pen: remember whether this item's panel was already open
      // before the tap began (focus may open it before the click arrives).
      el.addEventListener('pointerdown', function (e) {
        tap = e.pointerType === 'mouse' ? null : { key: key, wasOpen: openKey === key, at: Date.now() };
      });
      el.addEventListener('click', function (e) {
        var t = tap;
        tap = null;
        if (!t || t.key !== key || Date.now() - t.at > TAP_WINDOW || !isDesktop()) return;
        if (t.wasOpen) return; // second tap: follow the link
        e.preventDefault();
        open(key);
      });

      el.addEventListener('keydown', function (e) {
        if (!isDesktop() || e.altKey || e.ctrlKey || e.metaKey) return;
        var down = e.key === 'ArrowDown' || e.key === 'Down';
        if (!(down || (e.key === 'Tab' && !e.shiftKey))) return;
        if (openKey !== key) {
          if (!down) return; // Tab with the panel closed moves on as usual
          open(key);
        }
        var first = links(panelFor(key))[0];
        if (!first) return;
        e.preventDefault();
        first.focus();
      });
    });

    panels.forEach(function (p) {
      var key = p.getAttribute('data-menu-panel');

      // Re-entering the panel cancels the pending close (the original keepMenu).
      p.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse') clearTimeout(timer);
      });

      p.addEventListener('keydown', function (e) {
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        var items = links(p);
        var i = items.indexOf(document.activeElement);
        if (i < 0) return;

        if (e.key === 'Tab') {
          if (!e.shiftKey && i === items.length - 1) {
            var next = nextStop(key);
            if (next) {
              e.preventDefault();
              next.focus();
            }
          } else if (e.shiftKey && i === 0) {
            e.preventDefault();
            focusTrigger(key);
          }
        } else if (e.key === 'ArrowDown' || e.key === 'Down') {
          e.preventDefault();
          items[Math.min(i + 1, items.length - 1)].focus();
        } else if (e.key === 'ArrowUp' || e.key === 'Up') {
          e.preventDefault();
          if (i === 0) focusTrigger(key);
          else items[i - 1].focus();
        }
      });
    });

    root.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'mouse') mouseInside = true;
    });
    root.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse') return;
      mouseInside = false;
      close();
    });

    // Focus leaving both the nav items and the panels closes the menu, unless
    // a mouse is still over the header holding it open. Only a move to another
    // element counts: a tap on the panel's own text blurs the nav item with no
    // relatedTarget, and taps and clicks are handled by the pointerdown below.
    root.addEventListener('focusout', function (e) {
      if (openKey === null || mouseInside || !e.relatedTarget) return;
      if (inMenu(e.relatedTarget)) return;
      closeNow();
    });

    // Not in the original, but the panel is keyboard-reachable so it needs a
    // keyboard way out, and a click elsewhere should dismiss it.
    document.addEventListener('keydown', function (e) {
      if (!isEscape(e) || openKey === null) return;
      var key = openKey;
      var from = document.activeElement;
      closeNow();
      if (from && from.closest && from.closest('[data-menu-panel]') && root.contains(from)) focusTrigger(key);
    });
    // pointerdown rather than click: iOS does not send a click to the document
    // for a tap on something that is not itself clickable. A mouse still only
    // closes it from outside the header, as before; a tap closes it from
    // anywhere that is not a nav item or the open panel.
    document.addEventListener('pointerdown', function (e) {
      if (openKey === null) return;
      if (e.pointerType === 'mouse') {
        if (!root.contains(e.target)) closeNow();
      } else if (!inMenu(e.target)) {
        closeNow();
      }
    });
    window.addEventListener('resize', function () {
      if (!isDesktop()) closeNow();
      else paint();
    });

    paint();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-header-root]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', boot);
})();
